import { inject, Injectable } from '@angular/core';
import { AwsApiClient, TreatmentApiClient } from '../api-clients';
import { FileDownload } from '../models';
import { combineLatest, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import Jszip from 'jszip';

export function getPercentDone(loaded: number, total: number): number {
  return Math.round((100 * loaded) / total);
}

@Injectable({
  providedIn: 'root',
})
export class OrderFileDownloadService {
  private readonly awsClient = inject(AwsApiClient);
  private readonly _treatmentClient = inject(TreatmentApiClient);

  downloadAndZip(files: FileDownload[]) {
    return combineLatest(files.filter(f => !f.inProgress).map(f => this.download(f))).pipe(
      filter(progresses => progresses.every(p => !p.inProgress && p.blob)),
      switchMap(
        progresses =>
          new Observable<Blob>(observer => {
            const zipFile = new Jszip();
            progresses.forEach(({ name, blob }) => zipFile.file(name, blob!));
            zipFile
              .generateAsync({ type: 'blob' }, metadata => {})
              .then(
                v => {
                  observer.next(v);
                },
                err => observer.error(err)
              );
          })
      )
    );
  }

  download(file: FileDownload): Observable<FileDownload> {
    return of(file).pipe(
      tap(f => (f.inProgress = true)),
      switchMap(() => this._treatmentClient.getDownloadLink(file.id, file.extension)),
      switchMap(({ url }) =>
        this.awsClient.getS3File(url).pipe(
          map(event => {
            switch (event.type) {
              case HttpEventType.DownloadProgress:
                const percentDone = getPercentDone(event.loaded, event.total);
                return Object.assign(file, {
                  progress: percentDone,
                });
              case HttpEventType.Response:
                return Object.assign(file, {
                  progress: 100.0,
                  inProgress: false,
                  blob: new Blob([event.body]),
                });
              default:
                return file;
            }
          })
        )
      )
    );
  }
}
